import { useEffect, useState } from "react";
import { auth, db } from "../firebase";
import { doc, getDoc, setDoc, serverTimestamp, collection, getDocs } from "firebase/firestore";
import Nutrition from "./journal/Nutrition";
import Activities from "./journal/Activities";
import Finance from "./journal/Finance";
import Tasks from "./journal/Tasks";
import Mood from "./journal/Mood";

function getNextDay(dateStr) {
  const date = new Date(dateStr);
  date.setDate(date.getDate() + 1);
  return date.toISOString().split("T")[0];
}

export default function Journal({ date }) {
  const [data, setData] = useState(null);
  const [foods, setFoods] = useState([]);
  const [loading, setLoading] = useState(true);

  const user = auth.currentUser;

  useEffect(() => {
    if (!user || !date) return;
    setLoading(true);

    const journalRef = doc(db, "users", user.uid, "journal", date);
    const foodsRef = collection(db, "users", user.uid, "foods");

    Promise.all([getDoc(journalRef), getDocs(foodsRef)]).then(([journalSnap, foodsSnap]) => {
      // Load foods
      const foodsList = [];
      foodsSnap.forEach((doc) => {
        foodsList.push({ id: doc.id, ...doc.data() });
      });
      setFoods(foodsList);

      // Load journal
      if (journalSnap.exists()) {
        const journalData = journalSnap.data();
        // Migrate old nutrition format if needed
        if (typeof journalData.nutrition?.breakfast === "boolean") {
          journalData.nutrition = {
            breakfast: [],
            snack1: [],
            lunch: [],
            snack2: [],
            dinner: [],
            fruit: journalData.nutrition.fruit || false,
            healthy: journalData.nutrition.healthy || false
          };
        }
        setData(journalData);
      } else {
        setData({
          date,
          wakeUpTime: "",
          sleepTime: "",
          mood: { morning: 3, day: 3, evening: 3 },
          finance: { startingBalance: "" },
          income: [],
          expenses: [],
          nutrition: {
            breakfast: [],
            snack1: [],
            lunch: [],
            snack2: [],
            dinner: [],
            fruit: false,
            healthy: false
          },
          activities: {
            training: { active: false, duration: "" },
            learning: { active: false, duration: "" },
            social: { active: false, duration: "" }
          },
          tasks: [],
          notes: ""
        });
      }
      setLoading(false);
    });
  }, [user, date]);

  const addFood = async (food) => {
    const foodRef = doc(db, "users", user.uid, "foods", food.name);
    await setDoc(foodRef, { name: food.name, healthy: food.healthy });
    setFoods([...foods, { id: food.name, ...food }]);
  };

  // Kalkulacija završnog stanja
  const calculateEndingBalance = () => {
    const start = Number(data.finance?.startingBalance) || 0;
    const totalIncome = (data.income || []).reduce((sum, item) => sum + Number(item.amount), 0);
    const totalExpenses = (data.expenses || []).reduce((sum, item) => sum + Number(item.amount), 0);
    return start + totalIncome - totalExpenses;
  };

  const save = async () => {
    const ref = doc(db, "users", user.uid, "journal", date);
    const endingBalance = calculateEndingBalance();
    
    await setDoc(ref, {
      ...data,
      updatedAt: serverTimestamp()
    });

    // Postavi početno stanje za sutrašnji dan
    const nextDate = getNextDay(date);
    const nextRef = doc(db, "users", user.uid, "journal", nextDate);
    const nextSnap = await getDoc(nextRef);
    
    if (nextSnap.exists()) {
      // Ažuriraj samo startingBalance ako dan već postoji
      await setDoc(nextRef, {
        ...nextSnap.data(),
        finance: { ...nextSnap.data().finance, startingBalance: String(endingBalance) },
        updatedAt: serverTimestamp()
      });
    } else {
      // Kreiraj sutrašnji dan sa početnim stanjem
      await setDoc(nextRef, {
        date: nextDate,
        wakeUpTime: "",
        sleepTime: "",
        mood: { morning: 3, day: 3, evening: 3 },
        finance: { startingBalance: String(endingBalance) },
        income: [],
        expenses: [],
        nutrition: {
          breakfast: [],
          snack1: [],
          lunch: [],
          snack2: [],
          dinner: [],
          fruit: false,
          healthy: false
        },
        activities: {
          training: { active: false, duration: "" },
          learning: { active: false, duration: "" },
          social: { active: false, duration: "" }
        },
        tasks: [],
        notes: "",
        updatedAt: serverTimestamp()
      });
    }

    alert("Sačuvano! Sutrašnji dan ima početno stanje: " + endingBalance);
  };

  if (loading) return <p className="journal-container">Učitavanje...</p>;

  return (
    <div className="journal-container">
      <div className="journal-section">
        <h3>📅 {date}</h3>
        <div className="time-inputs">
          <label>
            🌅 Buđenje:
            <input
              type="time"
              value={data.wakeUpTime}
              onChange={(e) =>
                setData({ ...data, wakeUpTime: e.target.value })
              }
            />
          </label>
          <label>
            😴 Spavano:
            <input
              type="text"
              value={data.sleepTime || ""}
              placeholder="npr. 7h 30m"
              onChange={(e) =>
                setData({ ...data, sleepTime: e.target.value })
              }
            />
          </label>
        </div>
      </div>

      <div className="journal-section">
        <Mood
          value={data.mood}
          onChange={(mood) => setData({ ...data, mood })}
        />
      </div>

      <div className="journal-section">
        <Nutrition
          value={data.nutrition}
          onChange={(nutrition) => setData({ ...data, nutrition })}
          foods={foods}
          onAddFood={addFood}
        />
      </div>

      <div className="journal-section">
        <Activities
          value={data.activities}
          onChange={(activities) => setData({ ...data, activities })}
        />
      </div>

      <div className="journal-section">
        <Finance
          startingBalance={data.finance?.startingBalance || ""}
          income={data.income || []}
          expenses={data.expenses || []}
          onChangeStartingBalance={(val) => 
            setData({ ...data, finance: { ...data.finance, startingBalance: val } })
          }
          onChangeIncome={(income) => setData({ ...data, income })}
          onChangeExpenses={(expenses) => setData({ ...data, expenses })}
        />
      </div>

      <div className="journal-section">
        <Tasks
          value={data.tasks}
          onChange={(tasks) => setData({ ...data, tasks })}
        />
      </div>

      <div className="journal-section">
        <h3>📝 Zapažanja</h3>
        <textarea
          rows="5"
          value={data.notes}
          placeholder="Beleške za danas..."
          onChange={(e) =>
            setData({ ...data, notes: e.target.value })
          }
        />
      </div>

      <button className="save-button" onClick={save}>
        💾 Sačuvaj dan
      </button>
    </div>
  );
}
